var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { computed } from "mobx";
import { DocumentRetrievalLocationStatus } from "healthex-common/document.retrieval.dtos";
import { getObservableT } from "../Infra/i18next";
const t = getObservableT("patients");
export class DocumentRetrievalReviewAndShareViewModel {
    constructor(parentModel) {
        this.parentModel = parentModel;
    }
    get locationList() {
        return this.parentModel.locationList;
    }
    get loginRequiredLocations() {
        return this.parentModel.loginRequiredLocations;
    }
    get nonLoginRequiredLocations() {
        return this.parentModel.nonLoginRequiredLocations;
    }
    get allLocationsRequireLogin() {
        return this.parentModel.allLocationsRequireLogin;
    }
    get hasCompletedLoginCycle() {
        return this.parentModel.hasCompletedLoginCycle;
    }
    get anyLocationNeedsAuth() {
        return this.parentModel.anyLocationNeedsAuth;
    }
    get anyLocationAddedViaFasten() {
        return this.parentModel.anyLocationAddedViaFasten;
    }
    get shouldShowSectionLabels() {
        if (this.isStatic) {
            return true;
        }
        return this.hasCompletedLoginCycle || (this.locationList.length > 0 && this.loginRequiredLocations.length === 0);
    }
    get canAddLocations() {
        return this.parentModel.canAddLocations;
    }
    get organizationName() {
        return this.parentModel.organizationName;
    }
    get fastenPublicId() {
        return this.parentModel.fastenPublicId;
    }
    get fastenPatientId() {
        return this.parentModel.fastenPatientId;
    }
    get isStatic() {
        return this.parentModel.uiState.isStatic;
    }
    get supportingText() {
        var _a;
        if (!this.hasCompletedLoginCycle && this.loginRequiredLocations.length > 0) {
            return this.parentModel.isIAS
                ? t("We’ve found your records from the following providers. Some require you to login to access them.")
                : t("We've found your records from the following providers. Some require you to login to access and share them.");
        }
        return this.parentModel.isIAS
            ? t("Here’s a summary of the providers whose records we’ll access for your HealthEx Unified Record.")
            : t("Here's a summary of the providers whose records we'll share with {{ organizationName }}.", {
                organizationName: (_a = this.organizationName) !== null && _a !== void 0 ? _a : "the organization",
            });
    }
    get willBeSharedSectionText() {
        return this.parentModel.isIAS ? t("Will be accessed") : t("Will be shared");
    }
    get willNotBeSharedSectionText() {
        return this.parentModel.isIAS ? t("Will not be accessed") : t("Will not be shared");
    }
    get buttonText() {
        return !this.hasCompletedLoginCycle && this.loginRequiredLocations.length > 0
            ? t("Continue")
            : this.parentModel.isIAS
                ? t("Access records")
                : t("Share records");
    }
    get title() {
        return this.parentModel.isIAS ? t("Review and access your records") : t("Review and share your records");
    }
    get showAddProviderSection() {
        return this.canAddLocations && !this.anyLocationNeedsAuth;
    }
    get locationErrorTooltip() {
        return !this.hasCompletedLoginCycle
            ? t("This provider requires you to login to their portal to access records. Tap Continue to review providers.")
            : t("This provider was not logged in to. Their records will not be shared.");
    }
    get providerText() {
        if (this.anyLocationAddedViaFasten)
            return null;
        return this.showAddProviderSection
            ? t("HealthEx connects with most providers automatically, but some may not show up if they aren’t supported yet or have older details on file. Add providers to bring in the rest of your records.")
            : t("Missing a provider? HealthEx connects with most automatically, but some may not show up if they aren’t supported yet or have older details on file. You can add more after logging in to the ones above.");
    }
    get animate() {
        return this.parentModel.animate;
    }
    async handleContinueClick() {
        if (!this.isStatic && !this.hasCompletedLoginCycle && this.loginRequiredLocations.length > 0) {
            this.parentModel.continueFromReviewAndShare(this.locationList);
        }
        else {
            if (this.parentModel.onRecordsShared) {
                await this.parentModel.onRecordsShared();
            }
        }
    }
    shouldDelayLocation(location) {
        const isLoginRequired = location.status === DocumentRetrievalLocationStatus.NEEDS_AUTH || location.status === DocumentRetrievalLocationStatus.SKIPPED;
        return isLoginRequired && this.nonLoginRequiredLocations.length > 0;
    }
}
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "loginRequiredLocations", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "nonLoginRequiredLocations", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "shouldShowSectionLabels", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "supportingText", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "willBeSharedSectionText", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "willNotBeSharedSectionText", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "buttonText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "title", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "showAddProviderSection", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "locationErrorTooltip", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "providerText", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalReviewAndShareViewModel.prototype, "animate", null);
